import React, { useState } from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Chart, Dash } from "../../../types/Dash";
import Divider from "@mui/material/Divider";
import Grow from "@mui/material/Grow";

import useActions, { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectCurrentWorkspaceRole
} from "../../auth/authSlice";

export function ChartAdder(props: {
  organizationId: number;
  workspaceId: number;
  dash: Dash;
  pageNo: number,
  isFloatingInsert?: boolean | undefined;
  chartBelowInsert?: Chart
}) {
  const actions = useActions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentWorkspaceRole = useAppSelector(selectCurrentWorkspaceRole);

  // Check if workbook is associated with a file
  const isAssociatedWithFile = props.dash.repo_id !== null || props.dash.repo_file_name !== null;

  function addChartToEnd(codeLang = "RUST", chartType = "MULTILINE") {
    // Find the maximum component order
    const component_order =
      props.dash.charts!.length > 0
        ? Math.max(
            ...props.dash.charts!.map(function (o) {
              return o.chart_order;
            })
          ) + 10
        : 0;

    actions.createChart({
      name: "Untitled",
      chart_order: component_order,
      chart_type: chartType,
      data_source: "{}",
      code: "",
      code_result: "",
      code_error: "",
      code_lang: codeLang,
      page_no: props.pageNo,
      parameter_id: null,
      add_code_to_context: 1,
    });
  }

  function insertChart(codeLang = "RUST", chartType = "MULTILINE") {
    const newChart = {
      name: "Untitled",
      chart_order: props.chartBelowInsert!.chart_order,
      chart_type: chartType,
      data_source: "{}",
      code: "",
      code_result: "",
      code_error: "",
      code_lang: codeLang,
      page_no: props.pageNo,
      parameter_id: null,
      add_code_to_context: 1,

    };
    actions.createChartAboveChart({
      belowChart: props.chartBelowInsert!,
      insertedChart: newChart
    })
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isAssociatedWithFile) {
      setAnchorEl(event.currentTarget);
    } else {
      // Not associated with file, just add chart directly
      if(!props.isFloatingInsert) 
        addChartToEnd();
      else
        insertChart();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCreatePrompt = () => {
    handleClose();
    if(!props.isFloatingInsert) 
      addChartToEnd("PROMPT", "CODE");
    else
      insertChart("PROMPT", "CODE");
  };

  const handleCreateBasicChart = () => {
    handleClose();
    if(!props.isFloatingInsert) 
      addChartToEnd("RUST");
    else
      insertChart("RUST");
  };

  // Float style?
  const style = props.isFloatingInsert
    ? { mx: 15, height: 0, flexGrow: 1, zIndex: 1, position: 'relative'}
    : { mx: 15, height: "100vh" }

  return (
    <Box sx={style}>
      { (currentWorkspaceRole == "ADMIN") && <Grow in={true}>
        <Divider>
          <Button
            onClick={handleClick}
            onMouseDown={(e) => e.preventDefault()}
            startIcon={<AddCircleIcon fontSize="large" />}
            size="large"
          >
            {props.isFloatingInsert ? "Insert" : "Add"} Chart
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            <MenuItem onClick={handleCreatePrompt}>Create a prompt</MenuItem>
            <MenuItem onClick={handleCreateBasicChart}>Create a basic chart</MenuItem>
          </Menu>
        </Divider>
      </Grow> }
    </Box>
  );
}
